import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ADMIN_API_PREFIX, API_PREFIX, BASE_URL } from '@CONSTANTS/API.constant';
import userRepository from '@REPOSITORIES/local-repository/user-repository';
import { Region, CreateRegionParams, UpdateRegionParams } from '@INTERFACES/api/regions';

// Read API (uses API_PREFIX for read operations)
const regionsReadApi = createApi({
    reducerPath: 'regions-read-api',
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_URL + API_PREFIX,
        prepareHeaders: (headers) => {
            const token = userRepository.getData();

            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }

            return headers;
        },
    }),
    tagTypes: ['Region'],
    endpoints: (builder) => ({
        getAll: builder.query<Region[], void>({
            query: () => '/regions/',
            providesTags: ['Region'],
        }),
        getById: builder.query<Region, number>({
            query: (id) => `/regions/${id}/`,
            providesTags: (result, error, id) => [{ type: 'Region', id }],
        }),
    }),
});

// Write API (uses ADMIN_API_PREFIX for write operations)
const regionsWriteApi = createApi({
    reducerPath: 'regions-write-api',
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_URL + ADMIN_API_PREFIX,
        prepareHeaders: (headers) => {
            const token = userRepository.getData();

            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }

            return headers;
        },
    }),
    tagTypes: ['Region'],
    endpoints: (builder) => ({
        create: builder.mutation<Region, CreateRegionParams>({
            query: (params) => ({
                url: '/regions/',
                method: 'POST',
                body: params,
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: ['Region'],
        }),
        update: builder.mutation<Region, UpdateRegionParams>({
            query: ({ id, ...params }) => ({
                url: `/regions/${id}/`,
                method: 'PUT',
                body: params,
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Region', id }],
        }),
        partialUpdate: builder.mutation<Region, UpdateRegionParams>({
            query: ({ id, ...params }) => ({
                url: `/regions/${id}/`,
                method: 'PATCH',
                body: params,
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Region', id }],
        }),
        delete: builder.mutation<void, number>({
            query: (id) => ({
                url: `/regions/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Region'],
        }),
    }),
});

// Export both APIs for middleware and reducer registration
export const regionsAdminApi = regionsWriteApi; // Main API for backwards compatibility
export { regionsReadApi };

// Export hooks from both APIs
export const {
    useGetAllQuery: useGetAllRegions,
    
} = regionsReadApi;

export const {
    useCreateMutation: useCreateRegion,
    useUpdateMutation: useUpdateRegion,
    
    useDeleteMutation: useDeleteRegion,
} = regionsWriteApi;
